import { useEffect, useState } from "react";
import { apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle, UserMinus, UserCheck } from "lucide-react";
import { errorText } from "@/lib/calendar";

export const AssignSection = ({ ride, drivers, guard, onChanged }) => {
  const [driverId, setDriverId] = useState("");
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const canAssign = ["requested", "approved", "scheduled"].includes(ride.status);

  useEffect(() => {
    if (!driverId) {
      setCheck(null);
      return;
    }
    apiClient.get(`/rides/${ride.id}/conflicts`, { params: { driver_id: driverId } })
      .then(({ data }) => setCheck(data))
      .catch(() => setCheck(null));
  }, [driverId, ride.id]);

  const assign = async () => {
    setBusy(true);
    try {
      const res = await guard((force) => apiClient.post(`/rides/${ride.id}/employer-assign/${driverId}`, null, { params: { force } }));
      if (res) {
        toast.success(ride.driver_id ? "Driver changed. Calendar and notifications updated." : "Driver assigned. Ride scheduled.");
        setDriverId("");
        onChanged();
      }
    } catch (e) {
      toast.error(errorText(e, "Failed to assign driver"));
    } finally {
      setBusy(false);
    }
  };

  const unassign = async () => {
    setBusy(true);
    try {
      await apiClient.post(`/rides/${ride.id}/unassign`);
      toast.success("Driver removed. Ride is back in the unassigned list.");
      onChanged();
    } catch (e) {
      toast.error(errorText(e, "Failed to remove driver"));
    } finally {
      setBusy(false);
    }
  };

  if (!canAssign) return <p className="text-sm text-slate-500">Drivers can only be changed before the trip starts.</p>;

  return (
    <div className="space-y-3" data-testid="assign-section">
      <p className="text-sm text-slate-600">Current driver: <b>{ride.driver_name || "None"}</b></p>
      <Select value={driverId} onValueChange={setDriverId}>
        <SelectTrigger data-testid="detail-driver-select"><SelectValue placeholder="Select a driver" /></SelectTrigger>
        <SelectContent>
          {drivers.filter((d) => d.id !== ride.driver_id).map((d) => (
            <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {check && (check.conflicts.length > 0 || check.warnings.length > 0) && (
        <div className="space-y-1" data-testid="detail-conflict-warnings">
          {[...check.conflicts.map((c) => c.message), ...check.warnings].map((m, i) => (
            <p key={i} className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-2 flex gap-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />{m}
            </p>
          ))}
        </div>
      )}
      {check && check.conflicts.length === 0 && <p className="text-xs text-green-700" data-testid="detail-no-conflicts">No scheduling conflicts for this driver.</p>}
      <div className="flex gap-2 flex-wrap">
        <Button onClick={assign} disabled={!driverId || busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="detail-assign-btn">
          <UserCheck className="w-4 h-4 mr-2" />{ride.driver_id ? "Change Driver" : "Assign Driver"}
        </Button>
        {ride.driver_id && ride.status === "scheduled" && (
          <Button variant="outline" onClick={unassign} disabled={busy} data-testid="detail-unassign-btn">
            <UserMinus className="w-4 h-4 mr-2" />Remove Driver
          </Button>
        )}
      </div>
    </div>
  );
};

export default AssignSection;
